import { useMemo } from "react";
import AsyncSelect from "react-select/async";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { _ } from "i18n";
import { programGameClubAsyncStyles } from "./clubSelectApi";
import { PROGRAM_GAME_CLUB_TEAM_SIDE_ERRORS, ProgramGameClubTeamSide } from "./constants";
import { ProgramGameModalFieldError } from "./ProgramGameModalFieldError";
import type { ProgramGameFormErrors, ProgramGameSelectOption } from "./types";
import { normalizeProgramGameOption } from "./formUtils";
import { makeCreateClubNoOptionsMessage } from "./CreateClubNoOptionsMessage";

interface ProgramGameClubTeamFieldsProps {
	side: ProgramGameClubTeamSide;
	club: ProgramGameSelectOption | null;
	team: ProgramGameSelectOption | null;
	teamOptions: ProgramGameSelectOption[];
	teamsLoading: boolean;
	/** Selected club has no teams (e.g. a non-partner club) → team field is optional & disabled. */
	clubHasNoTeams: boolean;
	loadClubOptions: (inputValue: string) => Promise<ProgramGameSelectOption[]>;
	canCreateClub: boolean;
	onRequestCreateClub: (inputValue: string) => void;
	isSubmitted: boolean;
	formErrors: ProgramGameFormErrors;
	onClubChange: (club: ProgramGameSelectOption | null) => void;
	onTeamChange: (team: ProgramGameSelectOption | null) => void;
}

const SIDE_LABELS: Record<
	ProgramGameClubTeamSide,
	{ club: Parameters<typeof _>[0]; team: Parameters<typeof _>[0] }
> = {
	[ProgramGameClubTeamSide.Home]: {
		club: "discover.gamesTable.programGame.field.homeClub",
		team: "discover.gamesTable.programGame.field.homeTeam",
	},
	[ProgramGameClubTeamSide.Away]: {
		club: "discover.gamesTable.programGame.field.awayClub",
		team: "discover.gamesTable.programGame.field.awayTeam",
	},
};

export function ProgramGameClubTeamFields({
	side,
	club,
	team,
	teamOptions,
	teamsLoading,
	clubHasNoTeams,
	loadClubOptions,
	canCreateClub,
	onRequestCreateClub,
	isSubmitted,
	formErrors,
	onClubChange,
	onTeamChange,
}: ProgramGameClubTeamFieldsProps) {
	const sideErrors = PROGRAM_GAME_CLUB_TEAM_SIDE_ERRORS[side];
	const clubError = formErrors[sideErrors.club];
	const teamError = formErrors[sideErrors.team];
	const labels = SIDE_LABELS[side];
	const selectComponents = useMemo(
		() => ({ NoOptionsMessage: makeCreateClubNoOptionsMessage(canCreateClub, onRequestCreateClub) }),
		[canCreateClub, onRequestCreateClub],
	);

	return (
		<>
			<div className={`discoverGamesProgramModal-field discoverGamesProgramModal-field--${side}Club`}>
				<label className="discoverGamesProgramModal-label">
					{_(labels.club)} <span>*</span>
				</label>
				<AsyncSelect<ProgramGameSelectOption, false>
					className="react-select"
					classNamePrefix="react-select"
					placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
					value={club}
					loadOptions={loadClubOptions}
					defaultOptions
					cacheOptions
					menuPortalTarget={typeof document !== "undefined" ? document.body : null}
					styles={programGameClubAsyncStyles()}
					components={selectComponents}
					onChange={(option) => onClubChange(normalizeProgramGameOption(option))}
					isClearable
				/>
				<ProgramGameModalFieldError show={isSubmitted} message={clubError} />
			</div>

			<div className={`discoverGamesProgramModal-field discoverGamesProgramModal-field--${side}Team`}>
				<label className="discoverGamesProgramModal-label">
					{_(labels.team)} {!clubHasNoTeams && <span>*</span>}
				</label>
				<SelectComponent
					placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
					items={clubHasNoTeams ? [] : teamOptions}
					value={team}
					handleChange={(option) => onTeamChange(normalizeProgramGameOption(option))}
					isDisabled={!club || teamsLoading || clubHasNoTeams}
					isLoading={teamsLoading}
				/>
				<ProgramGameModalFieldError show={isSubmitted} message={teamError} />
			</div>
		</>
	);
}
